/**
 * F26 (Wave 2D / Decision 14) — admin notifications test-send page.
 *
 * Route: `/admin/notifications/test-send`
 * Permission: `notifications:dry_run` (super_admin only; gated by
 * ProtectedRoute in App.tsx).
 *
 * UX: form with category/kind/title/body/deepLink inputs (TestSendInputSchema
 * defaults pre-filled) → Submit → POST /admin/notifications/test-send. The
 * test event is delivered to the admin's own user_id; the admin must also
 * have a `users` row, else the route returns 422 TEST_SEND_NO_USER_ACCOUNT
 * which we surface as a friendly banner.
 *
 * Useful for verifying FCM creds end-to-end after AWS setup.
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  Select,
  Textarea,
  TextInput,
  useToast,
} from '@bravemove/ui-web';
import { ApiError } from '../../../auth/types';
import type { NotificationCategory, TestSendInput } from '@bravemove/types';
import { NOTIFICATION_CATEGORY_VALUES } from '@bravemove/types';
import { adminNotificationsApi } from '../../../api/notifications';
import { translateApiError } from '../../../auth/errors';
import { PageHeader } from '../../../components/PageHeader';

const DEFAULTS: Required<TestSendInput> = {
  category: 'system',
  kind: 'system.test_send',
  title: 'BraveMove test notification',
  body: 'This is a test from /admin/notifications/test-send.',
  deepLink: 'https://app.bravemove.app/notifications',
};

interface FieldErrors {
  kind?: string;
  title?: string;
  body?: string;
  deepLink?: string;
}

export function AdminNotificationsTestSendPage() {
  const { t } = useI18n();
  const toast = useToast();

  const [category, setCategory] = useState<NotificationCategory>(DEFAULTS.category);
  const [kind, setKind] = useState<string>(DEFAULTS.kind);
  const [title, setTitle] = useState<string>(DEFAULTS.title);
  const [body, setBody] = useState<string>(DEFAULTS.body);
  const [deepLink, setDeepLink] = useState<string>(DEFAULTS.deepLink);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [noUserAccount, setNoUserAccount] = useState(false);
  const [dispatchFailed, setDispatchFailed] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    if (!kind.trim()) errs.kind = 'Kind is required.';
    else if (kind.length > 120) errs.kind = 'Kind must be ≤120 characters.';
    if (!title.trim()) errs.title = 'Title is required.';
    else if (title.length > 200) errs.title = 'Title must be ≤200 characters.';
    if (!body.trim()) errs.body = 'Body is required.';
    else if (body.length > 500) errs.body = 'Body must be ≤500 characters.';
    if (!deepLink.trim()) errs.deepLink = 'Deep link is required.';
    else if (deepLink.length > 2048) errs.deepLink = 'Deep link must be ≤2048 characters.';
    return errs;
  }

  const onSubmit = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    setNoUserAccount(false);
    setDispatchFailed(false);
    setSuccess(false);

    try {
      await adminNotificationsApi.testSend({
        category,
        kind: kind.trim(),
        title: title.trim(),
        body: body.trim(),
        deepLink: deepLink.trim(),
      });
      setSuccess(true);
      toast.push({
        tone: 'success',
        title: t('admin:notifications.testSend.toast.success', {
          defaultValue: 'Test sent — check your inbox and (if registered) your device',
        }),
      });
    } catch (err) {
      // 422 TEST_SEND_NO_USER_ACCOUNT — admin must also have a `users` row.
      if (err instanceof ApiError && err.code === 'TEST_SEND_NO_USER_ACCOUNT') {
        setNoUserAccount(true);
      } else if (err instanceof ApiError && err.code === 'TEST_SEND_DISPATCH_FAILED') {
        // NT-04 — a kind with a legacy dispatcher rejected the synthetic
        // test payload AFTER the inbox row + push/email fan-out fired.
        setDispatchFailed(true);
      } else {
        setSubmitError(translateApiError(err, t));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-notifications-test-send-page">
      <PageHeader
        title={t('admin:notifications.testSend.title', {
          defaultValue: 'Send a test notification',
        })}
        subtitle={t('admin:notifications.testSend.subtitle', {
          defaultValue:
            'Send a test event to your own user account. Useful to verify FCM creds + your inbox plumbing.',
        })}
      />

      <Banner tone="info">
        {t('admin:notifications.testSend.banner', {
          defaultValue:
            'Super-admin only — this sends a real notification (writes an inbox row, may invoke FCM/SES).',
        })}
      </Banner>

      {noUserAccount ? (
        <div data-testid="admin-notifications-test-send-no-user-account">
          <Banner tone="warning">
            {t('notifications:errors.TEST_SEND_NO_USER_ACCOUNT', {
              defaultValue:
                "This admin doesn't have a BraveMove user account; test send needs one.",
            })}
          </Banner>
        </div>
      ) : null}

      {dispatchFailed ? (
        <div data-testid="admin-notifications-test-send-dispatch-failed">
          <Banner tone="warning">
            {t('admin:notifications.testSend.dispatchFailed', {
              defaultValue:
                "This kind has a production dispatcher that rejected the synthetic test payload — the inbox row (and push/email) may still have been delivered. Use a kind without a legacy dispatcher (e.g. 'system.test_send') to test channel plumbing.",
            })}
          </Banner>
        </div>
      ) : null}

      {success ? (
        <div data-testid="admin-notifications-test-send-success">
          <Banner tone="success">
            {t('admin:notifications.testSend.success', {
              defaultValue: 'Test event dispatched. Check your inbox.',
            })}
          </Banner>
        </div>
      ) : null}

      <div className="space-y-4">
        <FormField
          label={t('admin:notifications.testSend.fields.category', { defaultValue: 'Category' })}
          helper={t('admin:notifications.testSend.fields.categoryHint', {
            defaultValue:
              'Applied as the notification category (overrides the kind-derived category) — preferences and rate limits are evaluated against it.',
          })}
        >
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as NotificationCategory)}
            options={NOTIFICATION_CATEGORY_VALUES.map((c) => ({ value: c, label: c }))}
            data-testid="admin-notifications-test-send-category"
          />
        </FormField>

        <FormField
          label={t('admin:notifications.testSend.fields.kind', { defaultValue: 'Kind' })}
          error={fieldErrors.kind}
        >
          <TextInput
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            maxLength={120}
            data-testid="admin-notifications-test-send-kind"
          />
        </FormField>

        <FormField
          label={t('admin:notifications.testSend.fields.title', { defaultValue: 'Title' })}
          error={fieldErrors.title}
        >
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            data-testid="admin-notifications-test-send-title"
          />
        </FormField>

        <FormField
          label={t('admin:notifications.testSend.fields.body', { defaultValue: 'Body' })}
          error={fieldErrors.body}
        >
          <Textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
            data-testid="admin-notifications-test-send-body"
          />
        </FormField>

        <FormField
          label={t('admin:notifications.testSend.fields.deepLink', {
            defaultValue: 'Deep link',
          })}
          helper={t('admin:notifications.testSend.fields.deepLinkHint', {
            defaultValue: 'URL or in-app path the notification opens.',
          })}
          error={fieldErrors.deepLink}
        >
          <TextInput
            value={deepLink}
            onChange={(e) => setDeepLink(e.target.value)}
            maxLength={2048}
            data-testid="admin-notifications-test-send-deep-link"
          />
        </FormField>
      </div>

      {submitError ? <Banner tone="danger">{submitError}</Banner> : null}

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          loading={submitting}
          disabled={submitting}
          onClick={() => void onSubmit()}
          data-testid="admin-notifications-test-send-submit"
        >
          {t('admin:notifications.testSend.submit', { defaultValue: 'Send test' })}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setCategory(DEFAULTS.category);
            setKind(DEFAULTS.kind);
            setTitle(DEFAULTS.title);
            setBody(DEFAULTS.body);
            setDeepLink(DEFAULTS.deepLink);
            setFieldErrors({});
            setSubmitError(null);
            setSuccess(false);
            setNoUserAccount(false);
            setDispatchFailed(false);
          }}
        >
          {t('admin:common.reset', { defaultValue: 'Reset' })}
        </Button>
      </div>
    </div>
  );
}

export default AdminNotificationsTestSendPage;
